import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/api';
import { format } from 'date-fns';

const AuditLog = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Filters
  const [actionFilter, setActionFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState('');
  const [adminList, setAdminList] = useState([]);

  // Detail dialog
  const [detailDialog, setDetailDialog] = useState(null);

  // Load admin list for filter dropdown
  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const response = await adminAPI.listUsers({ is_super_admin: true, limit: 100 });
        setAdminList(response.data.users || []);
      } catch (err) {
        console.error('Failed to load admin list:', err);
      }
    };
    loadAdmins();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [page, rowsPerPage, actionFilter, targetFilter, adminFilter]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAuditLog({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        action: actionFilter || undefined,
        targetType: targetFilter || undefined,
        adminId: adminFilter || undefined,
      });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      console.error('Failed to load audit log:', err);
      setError('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const actionTypes = [
    'update_user',
    'send_password_reset',
    'set_temp_password',
    'update_subscription_quantity',
    'cancel_subscription',
    'issue_refund',
    'manually_verify_client',
    'transfer_client',
    'export_user_data',
  ];

  const targetTypes = ['user', 'subscription', 'client', 'verification'];

  const getActionColor = (action) => {
    if (action.includes('cancel') || action.includes('refund')) return 'error';
    if (action.includes('update') || action.includes('set')) return 'warning';
    if (action.includes('verify') || action.includes('send')) return 'success';
    return 'default';
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Audit Log
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Track all admin actions
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Action"
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
              size="small"
            >
              <MenuItem value="">All Actions</MenuItem>
              {actionTypes.map((action) => (
                <MenuItem key={action} value={action}>{action.replace(/_/g, ' ')}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Target Type"
              value={targetFilter}
              onChange={(e) => { setTargetFilter(e.target.value); setPage(0); }}
              size="small"
            >
              <MenuItem value="">All Targets</MenuItem>
              {targetTypes.map((target) => (
                <MenuItem key={target} value={target}>{target}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="By Admin"
              value={adminFilter}
              onChange={(e) => { setAdminFilter(e.target.value); setPage(0); }}
              size="small"
            >
              <MenuItem value="">All Admins</MenuItem>
              {adminList.map((admin) => (
                <MenuItem key={admin.id} value={admin.id}>{admin.email}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Log Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Admin</TableCell>
                <TableCell>Action</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Target</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>IP</TableCell>
                <TableCell align="center">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No audit logs found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(log.created_at), 'MMM d, yyyy')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(log.created_at), 'HH:mm:ss')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{log.admin_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={log.action.replace(/_/g, ' ')}
                        size="small"
                        color={getActionColor(log.action)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {log.target_identifier ? (
                        <Box>
                          <Typography variant="body2">
                            {log.target_identifier}
                          </Typography>
                          {log.target_id && (
                            <Tooltip title="View user">
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/users/${log.target_id}`)}
                              >
                                <OpenIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Typography variant="caption" color="text.secondary">
                        {log.ip_address || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {log.details && Object.keys(log.details).length > 0 ? (
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => setDetailDialog(log)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Action Details</DialogTitle>
        <DialogContent>
          {detailDialog && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {detailDialog.action} by {detailDialog.admin_email}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                {format(new Date(detailDialog.created_at), 'PPpp')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                  {JSON.stringify(detailDialog.details, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AuditLog;
